import React, { useState, useCallback } from "react";
import { Bot, User, Loader2, Plus, ChartBar, Table2, Gauge, BarChart3, Layout, AlignLeft, Grid3x3, Move, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatMessage, ComponentData, LayoutData } from "./types";
import { decodeHtmlEntities } from "@/utils/htmlEntities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  FOREST_GREEN,
  SAGE_GREEN,
  MID_GRAY,
  LIGHT_GRAY,
  CHARCOAL,
  WHITE,
  SHADOWS,
} from "@/styles/tokens";

interface AIChatMessageProps {
  message: ChatMessage;
  isLatest?: boolean;
  onPlaceComponent?: (componentData: ComponentData) => Promise<boolean>;
  onApplyLayout?: (layoutData: LayoutData) => void;
}

// Component type icon mapping
const getComponentIcon = (type: string | undefined) => {
  if (!type) return ChartBar;

  switch (type.toLowerCase()) {
    case 'line-chart':
      return ChartBar;
    case 'bar-chart':
      return BarChart3;
    case 'table':
      return Table2;
    case 'gauge-chart':
      return Gauge;
    default:
      return ChartBar;
  }
};

// Layout type icon mapping
const getLayoutIcon = (type: string | undefined) => {
  if (!type) return Layout;

  switch (type.toLowerCase()) {
    case 'grid':
      return Grid3x3;
    case 'align':
    case 'center':
      return AlignLeft;
    case 'move':
      return Move;
    default:
      return Layout;
  }
};

export const AIChatMessage: React.FC<AIChatMessageProps> = React.memo(({
  message,
  isLatest = false,
  onPlaceComponent,
  onApplyLayout,
}) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const avatarBackgroundColor = isUser ? 'hsl(var(--primary))' : 'hsl(var(--muted))';
  const avatarColor = isUser ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))';
  const bubbleBackgroundColor = isUser ? 'hsl(var(--primary))' : 'hsl(var(--muted))';
  const bubbleColor = isUser ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))';
  const bubbleBorder = isUser ? '1px solid transparent' : '1px solid hsl(var(--border))';
  const bubbleShadow = isUser
    ? '0 1px 2px hsl(var(--primary) / 0.18)'
    : '0 1px 2px hsl(var(--foreground) / 0.06)';

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may not be available */ }
  }, [message.content]);

  return (
    <div
      className={cn(
        "flex gap-2 p-2 rounded-lg",
        isUser && "flex-row-reverse",
        isLatest && "animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
      )}
    >
      {/* Avatar */}
      <div
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
        style={{
          backgroundColor: avatarBackgroundColor,
          color: avatarColor,
        }}
      >
        {isUser ? (
          <User className="w-3 h-3" />
        ) : (
          <Bot className="w-3 h-3" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={cn(
          "flex-1 min-w-0",
          isUser && "text-right"
        )}
      >
        <div
          className="inline-block px-3 py-2 rounded-lg max-w-[85%] break-words text-sm leading-snug"
          style={{
            backgroundColor: bubbleBackgroundColor,
            color: bubbleColor,
            border: bubbleBorder,
            boxShadow: bubbleShadow,
            position: 'relative',
            overflow: 'visible',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {message.isStreaming ? (
            <div className="flex items-center gap-2">
              <span>{decodeHtmlEntities(message.content)}</span>
              <Loader2 className="w-3 h-3 animate-spin" />
            </div>
          ) : (
            <span className="whitespace-pre-wrap leading-tight [&>ul]:space-y-0.5 [&>li]:my-0">{decodeHtmlEntities(message.content)}</span>
          )}
          {!message.isStreaming && isHovered && (
            <button
              onClick={handleCopy}
              aria-label="Copy message"
              title="Copy message"
              style={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: isUser ? '1px solid rgba(255,255,255,0.25)' : '1px solid hsl(var(--border))',
                cursor: 'pointer',
                padding: 0,
                backgroundColor: isUser ? 'hsl(var(--primary) / 0.92)' : 'hsl(var(--background))',
                boxShadow: isUser ? SHADOWS.sm : '0 1px 2px hsl(var(--foreground) / 0.08)',
                transition: 'opacity 150ms ease',
              }}
            >
              {copied ? (
                <Check size={14} color={isUser ? 'hsl(var(--primary-foreground))' : 'hsl(var(--primary))'} />
              ) : (
                <Copy size={14} color={isUser ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))'} />
              )}
            </button>
          )}
        </div>

        {/* Component Preview Card */}
        {isAssistant && message.componentData && !message.isStreaming && (
          <div className="mt-2 max-w-[85%]">
            <Card
              style={{
                border: `1px solid ${FOREST_GREEN.faint}`,
                backgroundColor: `${FOREST_GREEN.faint}33`,
                borderRadius: '8px',
              }}
            >
              <CardHeader className="pb-2 pt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {React.createElement(getComponentIcon(message.componentData.type), {
                      className: "w-3.5 h-3.5",
                      style: { color: FOREST_GREEN.DEFAULT }
                    })}
                    <CardTitle
                      className="text-xs font-medium"
                      style={{ color: FOREST_GREEN.hover }}
                    >
                      {message.componentData.title || 'Generated Component'}
                    </CardTitle>
                  </div>
                  <Badge
                    variant="secondary"
                    className="text-[10px] py-0 px-1.5 h-4"
                    style={{
                      backgroundColor: LIGHT_GRAY.dark,
                      color: CHARCOAL.DEFAULT,
                    }}
                  >
                    {message.componentData.type || 'component'}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-0 pb-2 px-3">
                {message.componentData.created ? (
                  // Component was already created and added to canvas
                  <div className="text-[11px] font-medium" style={{ color: FOREST_GREEN.DEFAULT }}>
                    ✓ Added to canvas
                  </div>
                ) : message.componentData.metadata?.canPlace ? (
                  <div className="space-y-1.5">
                    <div
                      className="text-[11px] leading-tight"
                      style={{ color: MID_GRAY.DEFAULT }}
                    >
                      <div>{message.componentData.placement?.size?.width || 400}×{message.componentData.placement?.size?.height || 300}px • ({message.componentData.placement?.position?.x || 50}, {message.componentData.placement?.position?.y || 50}) • {Math.round((message.componentData.metadata?.confidence || 0.5) * 100)}%</div>
                    </div>

                    <Button
                      onClick={() => {
                        if (message.componentData) {
                          void onPlaceComponent?.(message.componentData);
                        }
                      }}
                      size="sm"
                      className="w-full h-7 text-xs"
                      style={{
                        backgroundColor: FOREST_GREEN.DEFAULT,
                        color: WHITE,
                        border: 'none',
                        transition: 'background-color 150ms ease-in-out',
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add to Canvas
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-[11px] font-medium" style={{ color: '#D9534F' }}>
                      Component could not be generated
                    </div>
                    {message.componentData.error && (
                      <div className="text-[11px]" style={{ color: MID_GRAY.DEFAULT }}>
                        {message.componentData.error}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Layout Preview Card */}
        {isAssistant && message.layoutData && !message.isStreaming && (
          <div className="mt-2 max-w-[85%]">
            <Card
              style={{
                border: `1px solid ${SAGE_GREEN.faint}`,
                backgroundColor: `${SAGE_GREEN.faint}66`,
                borderRadius: '8px',
              }}
            >
              <CardHeader className="pb-2 pt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {React.createElement(getLayoutIcon(message.layoutData.type), {
                      className: "w-3.5 h-3.5",
                      style: { color: SAGE_GREEN.active }
                    })}
                    <CardTitle
                      className="text-xs font-medium"
                      style={{ color: SAGE_GREEN.active }}
                    >
                      {message.layoutData.description || 'Layout Arrangement'}
                    </CardTitle>
                  </div>
                  <Badge
                    variant="secondary"
                    className="text-[10px] py-0 px-1.5 h-4"
                    style={{
                      backgroundColor: LIGHT_GRAY.dark,
                      color: CHARCOAL.DEFAULT,
                    }}
                  >
                    {message.layoutData.type || 'layout'}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-0 pb-2 px-3">
                {message.layoutData.metadata?.canApply ? (
                  <div className="space-y-1.5">
                    <div
                      className="text-[11px] leading-tight"
                      style={{ color: MID_GRAY.DEFAULT }}
                    >
                      <div>{message.layoutData.arrangement} • {message.layoutData.instructions?.length || 0} actions • {Math.round((message.layoutData.metadata?.confidence || 0.5) * 100)}%</div>
                    </div>

                    <Button
                      onClick={() => {
                        if (message.layoutData) {
                          onApplyLayout?.(message.layoutData);
                        }
                      }}
                      size="sm"
                      className="w-full h-7 text-xs"
                      style={{
                        backgroundColor: SAGE_GREEN.DEFAULT,
                        color: CHARCOAL.DEFAULT,
                        border: 'none',
                        transition: 'background-color 150ms ease-in-out',
                      }}
                    >
                      <Layout className="w-3 h-3 mr-1" />
                      Apply Layout
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-[11px] font-medium" style={{ color: '#D9534F' }}>
                      Layout could not be generated
                    </div>
                    {message.layoutData.error && (
                      <div className="text-[11px]" style={{ color: MID_GRAY.DEFAULT }}>
                        {message.layoutData.error}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Timestamp */}
        <div
          className={cn(
            "text-[10px] mt-0.5",
            isUser ? "text-right" : "text-left"
          )}
          style={{ color: MID_GRAY.light }}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
});

AIChatMessage.displayName = "AIChatMessage";
